import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Eye, Sofa, HandHeart, ArrowLeft, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { WHY_VISIT_INTRO, WHY_VISIT, GALLERY, STATS, IMAGES, LINKS } from "../data/site";

const icons = { eye: Eye, sofa: Sofa, handheart: HandHeart };

export const WhyVisit = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section id="bemutatoterem" data-testid="why-visit-section" className="scroll-mt-24 py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-start gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow mb-3">{WHY_VISIT_INTRO.eyebrow}</p>
            <h2 className="font-display text-3xl font-semibold leading-tight text-ink sm:text-5xl">
              {WHY_VISIT_INTRO.title}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-soft sm:text-lg">{WHY_VISIT_INTRO.text}</p>

            <div className="mt-9 space-y-7">
              {WHY_VISIT.map((item, i) => {
                const Icon = icons[item.icon];
                return (
                  <motion.div
                    key={item.title}
                    data-testid={`why-visit-reason-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                    className="flex gap-4"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-burgundy/10 text-burgundy">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-semibold text-ink">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-soft sm:text-base">{item.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Reveal>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative mx-auto max-w-xl">
              <div className="embla relative rounded-3xl card-shadow-lg" ref={emblaRef} data-testid="showroom-gallery">
                <div className="embla__container">
                  {GALLERY.map((img, i) => (
                    <div key={img.src} className="embla__slide">
                      <img
                        src={img.src}
                        alt={img.alt}
                        loading="lazy"
                        data-testid={`gallery-slide-${i}`}
                        className="h-[220px] w-full object-cover sm:h-[300px]"
                      />
                    </div>
                  ))}
                </div>
                <span
                  data-testid="gallery-counter"
                  className="absolute right-4 top-4 rounded-full bg-night/70 px-3.5 py-1.5 text-xs font-bold text-cream backdrop-blur-sm"
                >
                  {selected + 1} / {GALLERY.length}
                </span>
                <button
                  data-testid="gallery-prev"
                  aria-label="Előző kép"
                  onClick={() => emblaApi && emblaApi.scrollPrev()}
                  className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-ink transition-colors hover:bg-burgundy hover:text-cream"
                >
                  <ArrowLeft className="h-[18px] w-[18px]" />
                </button>
                <button
                  data-testid="gallery-next"
                  aria-label="Következő kép"
                  onClick={() => emblaApi && emblaApi.scrollNext()}
                  className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-ink transition-colors hover:bg-burgundy hover:text-cream"
                >
                  <ArrowRight className="h-[18px] w-[18px]" />
                </button>
              </div>

              <div
                className="absolute -bottom-9 -left-6 hidden w-64 overflow-hidden rounded-2xl border-4 border-white card-shadow-lg sm:block lg:-left-14"
                data-testid="store-photo"
              >
                <img
                  src={IMAGES.exterior}
                  alt="A keszthelyi áruház épülete kívülről – Comfort Line Bútoráruház"
                  loading="lazy"
                  className="h-40 w-full object-cover"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-col items-center gap-3">
              <div className="flex gap-2" data-testid="gallery-dots">
                {GALLERY.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-dot-${i}`}
                    aria-label={`Ugrás a(z) ${i + 1}. képre`}
                    onClick={() => emblaApi && emblaApi.scrollTo(i)}
                    className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                      i === selected ? "w-7 bg-burgundy" : "w-2 bg-ink/20 hover:bg-burgundy/50"
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs font-semibold text-soft" data-testid="gallery-more-note">
                További képek áruházunkról a comfortbutor.hu-n érhetők el.
              </p>
            </div>
          </motion.div>
        </div>

        <Reveal className="mt-24 grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="stats-grid">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`stat-${i}`}
              className="rounded-3xl border border-line bg-white p-6 text-center card-shadow"
            >
              <p className="font-display text-3xl font-semibold text-burgundy sm:text-4xl">{s.value}</p>
              <p className="mt-1 text-sm font-semibold text-soft">{s.label}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
};
